<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'update:show'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    targetInbox: {
      required,
    },
  },
  data() {
    return {
      targetInbox: null,
      isMoving: false,
      movableInboxes: [],
      isLoadingInboxes: false,
    };
  },
  computed: {
    ...mapGetters({ currentChat: 'getSelectedChat' }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchMovableInboxes();
      }
    },
  },
  mounted() {
    if (this.show) {
      this.fetchMovableInboxes();
    }
  },
  methods: {
    async fetchMovableInboxes() {
      if (!this.currentChat?.id) return;
      this.isLoadingInboxes = true;
      try {
        const { data } = await ConversationApi.movableInboxes(
          this.currentChat.id
        );
        this.movableInboxes = data;
      } catch (error) {
        Sentry.captureException(error);
        this.movableInboxes = [];
      } finally {
        this.isLoadingInboxes = false;
      }
    },
    onClose() {
      this.targetInbox = null;
      this.movableInboxes = [];
      this.v$.$reset();
      this.$emit('close');
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isMoving = true;
      try {
        await this.$store.dispatch('moveConversation', {
          conversationId: this.currentChat.id,
          targetInboxId: this.targetInbox.id,
        });
        useAlert(this.$t('MOVE_CONVERSATION.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        Sentry.captureException(error);
        useAlert(this.$t('MOVE_CONVERSATION.ERROR_MESSAGE'));
      } finally {
        this.isMoving = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" @close="onClose">
    <woot-modal-header
      :header-title="$t('MOVE_CONVERSATION.TITLE')"
      :header-content="$t('MOVE_CONVERSATION.DESCRIPTION')"
    />

    <form class="flex flex-col gap-4 px-8 py-4" @submit.prevent="onSubmit">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('MOVE_CONVERSATION.SOURCE.TITLE') }}
        </label>
        <div
          class="flex items-center gap-3 p-3 border rounded-lg border-n-strong bg-n-alpha-2"
        >
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('MOVE_CONVERSATION.SOURCE.ID_LABEL') }}: #{{
                currentChat.id
              }}
            </span>
            <span class="text-sm truncate text-n-slate-11">
              {{ currentChat.meta.sender.name }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('MOVE_CONVERSATION.TARGET.TITLE') }}
        </label>
        <div
          class="multiselect-wrap--medium"
          :class="{ error: v$.targetInbox.$error }"
        >
          <multiselect
            v-model="targetInbox"
            :options="movableInboxes"
            :loading="isLoadingInboxes"
            label="name"
            track-by="id"
            :show-labels="false"
            :placeholder="$t('MOVE_CONVERSATION.TARGET.PLACEHOLDER')"
            allow-empty
            :max-height="200"
          >
            <template #singleLabel="props">
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ props.option.name }}</span>
              </div>
            </template>
            <template #option="props">
              <div class="flex items-center gap-2 py-1">
                <span>{{ props.option.name }}</span>
              </div>
            </template>
            <template #noResult>
              <span>
                {{ $t('MOVE_CONVERSATION.TARGET.NO_RESULTS') }}
              </span>
            </template>
          </multiselect>
          <span v-if="v$.targetInbox.$error" class="message">
            {{ $t('MOVE_CONVERSATION.TARGET.ERROR') }}
          </span>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('MOVE_CONVERSATION.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :is-loading="isMoving"
          :label="$t('MOVE_CONVERSATION.SUBMIT')"
        />
      </div>
    </form>
  </woot-modal>
</template>

<style lang="scss" scoped>
.error .message {
  @apply mt-1 text-sm text-n-ruby-11;
}

::v-deep {
  .multiselect {
    @apply rounded-md;
  }

  .multiselect__tags {
    @apply h-auto;
  }

  .multiselect__select {
    @apply mt-px mr-1;
  }

  .multiselect__option {
    @apply min-h-0 py-2;
  }
}
</style>
